import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import type { Async } from '../lib/useAsync.js';
import { ago, count, exact, numeric } from '../lib/format.js';
import { avatarColor, initials } from '../lib/avatar.js';
import {
  PLATFORMS,
  isKnownPlatform,
  parsePlatforms,
  platformLabel,
  type DeviceKind,
  type Platform,
} from '../lib/platforms.js';

/**
 * The primitives, and one component that enforces a rule.
 *
 * Colours come from `@ridgewill/brand` rather than being written here. The old
 * portal stored a hex value on every application row (`iconBg`) and on every
 * activity entry (`dot`), which made a rebrand a database migration. The same
 * mistake is available in a component file, and this is where it would go.
 */

/**
 * Renders the three states, and makes collapsing them impossible.
 *
 * A screen cannot accidentally show an empty table for a failed request,
 * because it never sees `data` unless the fetch succeeded. That is the defect
 * this project shipped twice — the login screen reporting a 500 as "signed
 * out" (auth#32), and the old portal's `isLoading ? '—' : d!.stats`.
 *
 * An empty list means the server said there is nothing. A failure means nobody
 * knows. They must never look the same.
 */
export function AsyncBoundary<T>({
  state,
  children,
  empty,
}: {
  state: Async<T>;
  children: (data: T) => ReactNode;
  empty?: ReactNode;
}) {
  if (state.status === 'loading') return <Note>Loading…</Note>;
  if (state.status === 'failed') return <Note tone="danger">{state.message}</Note>;
  if (empty !== undefined && Array.isArray(state.data) && state.data.length === 0) {
    return <Note>{empty}</Note>;
  }
  return <>{children(state.data)}</>;
}

/**
 * A block of prose that is telling you something, at one of three volumes.
 *
 * `warn` arrived with org-admin#56 and the distinction it draws is the point:
 * **danger is something that has gone wrong; warn is something that is about to
 * need you.** A partitioning threshold being crossed breaks nothing — the
 * platform is working, it has simply reached the size at which a deferred
 * decision stops being deferrable. Painting that red says outage, and a screen
 * that cries outage over a capacity signal is one people stop reading.
 */
export function Note({ children, tone }: { children: ReactNode; tone?: 'danger' | 'warn' }) {
  const tones = {
    danger: { fg: C.dangerText, bg: C.dangerBg, br: C.dangerBorder },
    warn: { fg: C.warnText, bg: C.warnBg, br: C.warnDot },
  };
  const t = tone ? tones[tone] : null;
  return (
    <p
      style={{
        margin: 0,
        padding: '1.25rem 1.4rem',
        color: t ? t.fg : C.muted,
        background: t ? t.bg : 'transparent',
        border: t ? `1px solid ${t.br}` : 'none',
        borderRadius: 8,
        lineHeight: 1.5,
      }}
    >
      {children}
    </p>
  );
}


export function Panel({
  title,
  info,
  action,
  children,
}: {
  title: string;
  /**
   * An explanation of the panel, next to its TITLE.
   *
   * Its own slot rather than something a caller drops into `action`, because
   * `action` is the right-hand group: a bubble put there ends up separated from
   * the words it explains by the full width of the panel, pointing at nothing.
   *
   * Long prose belongs here rather than in the body — see the Security groups
   * screen, where a three-line description sat above a two-row table and the
   * explanation was bigger than the thing explained.
   */
  info?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section style={s.panel}>
      <header style={s.panelHead}>
        <div style={s.panelTitleGroup}>
          <h2 style={s.panelTitle}>{title}</h2>
          {info}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * Somebody's face, or their monogram.
 *
 * ── The photograph is the point, and the fallback is the common case ──
 *
 * `user.image` is set only by an avatar upload from the account portal, so most
 * accounts here have none. The monogram is therefore not a placeholder for a
 * missing thing — it IS the avatar for the majority, and it has to look
 * deliberate rather than broken. Same `avatarColor` hash the sidebar uses, so
 * one account is the same colour everywhere, forever, with nothing stored.
 *
 * ── A broken image URL falls back too ────────────────────────────────
 *
 * `onError` swaps to the monogram. The URL points at a public R2 object that
 * could have been purged, and the browser's broken-image glyph beside somebody's
 * name reads as a fault in the portal rather than in the bucket.
 *
 * `alt=""` and `aria-hidden`: the account is named in text a few pixels away, so
 * a screen reader announcing it twice is noise. This is decoration that happens
 * to be informative.
 *
 * ── NO RING (Jon, 2026-08-28) ────────────────────────────────────────
 *
 * The first version carried the sidebar's turquoise halo. A ring is a status
 * cue everywhere else on this surface — the active nav rail, a DotPill — so one
 * drawn round every avatar unconditionally says something and means nothing,
 * and it competes with the `active`/`banned` pill sitting a few pixels away
 * from it in the record header.
 */
export function Avatar({
  id,
  label,
  image,
  size = 40,
}: {
  /** What the colour is hashed from — the account id, so it is stable across a
   *  rename. */
  id: string;
  /** The name or email the initials come from. */
  label: string;
  image?: string | null;
  size?: number;
}) {
  const [broken, setBroken] = useState(false);

  const frame = {
    width: size,
    height: size,
    borderRadius: '50%',
    flex: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden' as const,
  };

  if (image && !broken) {
    return (
      <img
        src={image}
        alt=""
        aria-hidden
        onError={() => setBroken(true)}
        style={{ ...frame, objectFit: 'cover' }}
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{
        ...frame,
        background: avatarColor(id),
        color: '#fff',
        fontSize: Math.round(size * 0.36),
        fontWeight: 700,
        letterSpacing: '.02em',
      }}
    >
      {initials(label)}
    </span>
  );
}

/**
 * The platforms an application runs on, as badges.
 *
 * Reads the stored column rather than taking a parsed array, because every
 * caller has the raw string and none of them has a reason to parse it — and one
 * of them parsing it differently is how the same application comes to look like
 * two on two screens.
 *
 * An empty column renders the same muted dash the rest of this portal uses for
 * "not stated". A value the vocabulary does not know renders as itself: it is
 * what the record holds, and hiding it would make the screen disagree with the
 * database (lib/platforms.ts).
 */
export function Platforms({ value }: { value: string | null | undefined }) {
  const values = parsePlatforms(value);
  if (values.length === 0) return <span style={{ color: C.muted }}>&mdash;</span>;
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      {values.map((v) => (
        <Badge key={v} tone={isKnownPlatform(v) ? 'neutral' : 'warn'}>
          {platformLabel(v)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Ticking the platforms an application runs on: a grid of kind of device by
 * system (Jon chose it from three mock-ups, 2026-10-23).
 *
 * ── Phones and tablets are separate platforms ────────────────────────
 *
 * Rows are Phone, Tablet and Computer, columns are Android, iOS, Windows and
 * macOS, and a pair that does not exist (a Windows phone) is a dash rather
 * than a box. Web is a row of its own: any browser, any device.
 *
 * ── A value we do not recognise is shown, ticked, and marked ─────────
 *
 * Same rule as `GrantCheckboxes` directly below, and it matters more here
 * because this field was free text until 2026-08: whatever anybody typed is in
 * the column, and a control that renders only its own vocabulary would hide it
 * while the next save deleted it.
 */
const GRID_ROWS: { kind: DeviceKind; label: string }[] = [
  { kind: 'phone', label: 'Phone' },
  { kind: 'tablet', label: 'Tablet' },
  { kind: 'computer', label: 'Computer' },
];
const GRID_COLUMNS: { system: NonNullable<Platform['system']>; label: string }[] = [
  { system: 'android', label: 'Android' },
  { system: 'ios', label: 'iOS / iPadOS' },
  { system: 'windows', label: 'Windows' },
  { system: 'macos', label: 'macOS' },
];

export function PlatformGrid({
  picked,
  onChange,
}: {
  picked: string[];
  onChange: (next: string[]) => void;
}) {
  const extra = picked.filter((v) => !isKnownPlatform(v));
  const toggle = (v: string) =>
    onChange(picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]);
  const cell: React.CSSProperties = { padding: '.4rem .7rem', textAlign: 'center', borderTop: `1px solid ${C.border}` };
  const head: React.CSSProperties = { ...cell, borderTop: 'none', fontSize: '.78rem', fontWeight: 600, color: C.textMid };
  const rowHead: React.CSSProperties = { ...cell, textAlign: 'left', fontSize: '.85rem', fontWeight: 600, color: C.textMid };

  return (
    <div style={{ display: 'grid', gap: '.6rem' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '.86rem', width: 'max-content', maxWidth: '100%' }}>
        <thead>
          <tr>
            <th style={head} />
            {GRID_COLUMNS.map((c) => (
              <th key={c.system} scope="col" style={head}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {GRID_ROWS.map((r) => (
            <tr key={r.kind}>
              <th scope="row" style={rowHead}>
                {r.label}
              </th>
              {GRID_COLUMNS.map((c) => {
                const p = PLATFORMS.find((x) => x.device === r.kind && x.system === c.system);
                return (
                  <td key={c.system} style={cell}>
                    {p ? (
                      <input
                        type="checkbox"
                        aria-label={p.label}
                        checked={picked.includes(p.value)}
                        onChange={() => toggle(p.value)}
                        style={{ width: 16, height: 16, margin: 0, cursor: 'pointer' }}
                      />
                    ) : (
                      <span aria-hidden style={{ color: C.muted }}>
                        –
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr>
            <th scope="row" style={rowHead}>
              Web
            </th>
            <td colSpan={GRID_COLUMNS.length} style={{ ...cell, textAlign: 'left' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '.45rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={picked.includes('web')}
                  onChange={() => toggle('web')}
                  style={{ width: 16, height: 16, margin: 0 }}
                />
                Any browser
              </label>
            </td>
          </tr>
        </tbody>
      </table>
      {extra.length ? (
        <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap' }}>
          {extra.map((v) => (
            <label
              key={v}
              style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontSize: '.86rem' }}
              title="Typed into this field while it was free text. Untick it to drop it, or leave it — it is kept either way."
            >
              <input type="checkbox" checked onChange={() => toggle(v)} />
              {v}
              <Badge tone="warn">unrecognised</Badge>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Padding for panel content that is NOT a table.
 *
 * A `Panel` has none of its own, because the thing it usually holds is a table
 * whose own cells carry it and whose rules have to reach both edges. Anything
 * else — a paragraph, a form, an outcome note — sits flush against the border
 * without this, which is org-admin#74: prose hard against the panel edge, spotted
 * from a screenshot one commit after a patch claiming it was fixed.
 *
 * A component rather than a style object each screen copies, so the number is in
 * one place and matches the table's `1.2rem` gutter by construction.
 */
export function PanelBody({ children }: { children: ReactNode }) {
  return <div style={{ padding: '.9rem 1.2rem', display: 'grid', gap: '.8rem' }}>{children}</div>;
}

/**
 * A table, not a grid of divs.
 *
 * The old portal built rows from nested flex containers with a shared
 * `gridTemplateColumns` string repeated in the header and every row. Real table
 * semantics cost nothing here and give column alignment, keyboard navigation
 * and screen-reader headers for free.
 */
export function Table({
  columns,
  children,
  actionsLast,
}: {
  columns: ReactNode[];
  children: ReactNode;
  /**
   * The last column holds row actions, so pin it to the right edge.
   *
   * ── WHY A FLAG AND NOT JUST `text-align: right` ──────────────────
   *
   * A table at `width: 100%` with auto layout distributes slack across every
   * column in proportion to its content. An action column at the end therefore
   * sits wherever the columns before it happen to finish — about three quarters
   * across on a group's tables — with a band of empty table to its right. It
   * reads as a button that failed to line up, because it is one.
   *
   * Aligning the TEXT right does nothing: the cell is already only as wide as
   * the button. What moves it is `width: 1%`, which asks the layout for the
   * column's minimum and hands the slack to the others — the standard idiom, and
   * the reason this is one flag rather than a width on every caller.
   *
   * Opt-in rather than automatic, because a trailing column is not always an
   * action: it can be the last of several data columns, and shrinking that one
   * would crush real content against the edge.
   */
  actionsLast?: boolean;
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={s.table}>
        <thead>
          <tr>
            {/* Indexed keys, because a header can be a control rather than a
                string — the select-all checkbox has no text to key on, and
                blank columns would otherwise collide. */}
            {columns.map((c, i) => (
              <th
                key={i}
                style={
                  actionsLast && i === columns.length - 1 ? { ...s.th, ...s.actionCol } : s.th
                }
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Td({
  children,
  mono,
  action,
  top,
}: {
  children: ReactNode;
  mono?: boolean;
  /** The row-action cell. Pairs with `actionsLast` on the Table — both are
   *  needed, because a header that shrinks while its cells do not leaves the
   *  column split between two widths. */
  action?: boolean;
  /**
   * Align to the TOP of the row rather than its middle.
   *
   * For a table with one tall column and several short ones — the notes list,
   * where a note runs to several lines and its date and author are three words
   * (Jon, 2026-08-28). Centred, the short cells float in the middle of a tall
   * row and stop marking where it begins, so two long notes in sequence read as
   * one block of prose with some grey text loose beside it.
   */
  top?: boolean;
}) {
  return (
    <td
      style={{
        ...s.td,
        ...(mono ? { fontFamily: 'ui-monospace, monospace', fontSize: '.82rem' } : {}),
        ...(action ? s.actionCol : {}),
        ...(top ? { verticalAlign: 'top' as const } : {}),
      }}
    >
      {children}
    </td>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'danger' }) {
  const tones = {
    neutral: { bg: C.page, fg: C.textMid, br: C.border },
    good: { bg: C.successBg, fg: C.successText, br: C.successBg },
    warn: { bg: C.warnBg, fg: C.warnText, br: C.warnBg },
    danger: { bg: C.dangerBg, fg: C.dangerText, br: C.dangerBorder },
  }[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '.12rem .45rem',
        borderRadius: 4,
        fontSize: '.75rem',
        fontWeight: 600,
        background: tones.bg,
        color: tones.fg,
        border: `1px solid ${tones.br}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * `href` and `onClick` are mutually exclusive, and `disabled` belongs only to
 * the button.
 *
 * A link cannot be disabled — `<a>` has no such attribute, and the usual
 * workarounds (aria-disabled, pointer-events: none) leave a control that is
 * still focusable, still followable by keyboard, or silently inert. So the type
 * refuses the combination rather than the implementation faking it.
 */
type ButtonProps = {
  children: ReactNode;
  tone?: 'danger';
  /**
   * Why this button cannot be pressed.
   *
   * For controls that are disabled by a RULE rather than by a transient state —
   * a system group that cannot be deleted, say. A disabled control with no
   * explanation reads as a broken feature, and the person goes looking for the
   * answer somewhere the answer is not.
   */
  title?: string;
} & (
  | {
      onClick: () => void;
      href?: never;
      disabled?: boolean;
    }
  | {
      /** Where it goes. A hash route: `#/activity?user=abc123`. */
      href: string;
      onClick?: never;
      disabled?: never;
    }
);

/**
 * A secondary control, which is sometimes a LINK.
 *
 * ── Why the link variant exists ──────────────────────────────────────
 *
 * "View activity", "View account", "View actor" were buttons calling
 * `navigate()` while the detail views were modals — they had to be, because
 * closing a dialog and navigating are two actions and only one of them is an
 * address. Now that the detail views are screens, those are destinations, and a
 * destination rendered as a button loses everything the browser gives an anchor:
 * middle click, open in a new tab, copy link, and a status bar that says where
 * it goes before you commit.
 *
 * Same styling either way, deliberately. These do the same job in the same row
 * and a person should not have to know which is which to predict what a click
 * does.
 */
export function Button({ children, onClick, href, tone, disabled, title }: ButtonProps) {
  const style = {
    padding: '.35rem .7rem',
    fontSize: '.85rem',
    fontWeight: 600,
    borderRadius: 5,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    background: 'transparent',
    color: tone === 'danger' ? C.dangerText : C.brand,
    border: `1px solid ${tone === 'danger' ? C.dangerBorder : C.inputBorder}`,
  };

  if (href !== undefined) {
    return (
      // `display: inline-block`, or the padding on an inline anchor does not
      // push its neighbours apart and the border overlaps the line above.
      <a href={href} title={title} style={{ ...style, display: 'inline-block', textDecoration: 'none' }}>
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={style}>
      {children}
    </button>
  );
}

/**
 * A button that has to be HELD, not clicked.
 *
 * ── What it is for, and why a modal is not the answer here ───────────
 *
 * Removing somebody from a security group withdraws whatever that group gave
 * them, and on `Basic Users` that is every account on the platform's universal
 * entitlement. The control sits at the end of a row in a list somebody is
 * scrolling and searching, which is the worst possible neighbourhood for an
 * irreversible one-click action.
 *
 * A confirmation dialog is the usual answer and it is the wrong one for a row
 * action: it is two clicks, both of them reflexes, and the second is made
 * looking at a dialog rather than at the row. Holding is a different KIND of
 * input — it cannot be produced by a mis-click, it cannot be produced by a
 * reflex, and the thing you are looking at while you do it is the row itself.
 *
 * ── The fill is the instruction ──────────────────────────────────────
 *
 * The label reads as an ordinary one ("Remove"), so the FILL is the only thing
 * that tells a person what this control wants: it starts moving under the
 * pointer on the first press, and the `title` says how long. A button that
 * appears to do nothing on click is indistinguishable from a broken one, which
 * is what this is if the fill is ever removed.
 *
 * Releasing early resets to zero rather than holding the progress: partial
 * progress that persists invites a second short press to "finish it off", which
 * is the reflex this exists to defeat.
 *
 * ── It must be reachable without a mouse ─────────────────────────────
 *
 * Space and Enter held down drive the same timer. `event.repeat` is ignored, or
 * the browser's own key-repeat would restart the hold on every tick and it would
 * never complete. Without this the action is mouse-only, which is not a
 * confirmation design, it is an exclusion.
 */
export function HoldButton({
  children,
  onHold,
  holdMs = 1500,
  disabled,
  title,
  tone = 'danger',
}: {
  children: ReactNode;
  onHold: () => void;
  holdMs?: number;
  disabled?: boolean;
  title?: string;
  /** `brand` for an irreversible action that is not a removal: sending an
   *  email to a list (Group email). Same mechanics, the primary colour. */
  tone?: 'danger' | 'brand';
}) {
  const fill = tone === 'brand' ? C.brand : C.danger;
  const ink = tone === 'brand' ? C.brand : C.dangerText;
  const edge = tone === 'brand' ? C.brand : C.dangerBorder;
  const [progress, setProgress] = useState(0);
  const frame = useRef(0);
  const fired = useRef(false);

  const stop = useCallback(() => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = 0;
    setProgress(0);
  }, []);

  const begin = useCallback(() => {
    if (disabled || frame.current) return;
    fired.current = false;
    const startedAt = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - startedAt) / holdMs);
      setProgress(p);
      if (p < 1) {
        frame.current = requestAnimationFrame(tick);
        return;
      }
      // Cleared BEFORE the callback: `onHold` typically unmounts this row, and a
      // frame still queued against a gone component is the warning that trains
      // people to switch StrictMode off.
      frame.current = 0;
      setProgress(0);
      if (!fired.current) {
        fired.current = true;
        onHold();
      }
    };
    frame.current = requestAnimationFrame(tick);
  }, [disabled, holdMs, onHold]);

  useEffect(() => () => {
    if (frame.current) cancelAnimationFrame(frame.current);
  }, []);

  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      // Announced as what it is, because "Remove" alone would be a promise the
      // control does not keep for anyone who clicks it once.
      aria-label={typeof children === 'string' ? `${children}, hold to confirm` : undefined}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        try {
          // Captured, so a hold that drifts a few pixels off the button still
          // delivers its pointerup here and resets rather than sticking.
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // Throws when the pointer id is not active — a synthetic event, or an
          // assistive device that reports one. Capture is a refinement; losing
          // it costs a hold that drifts off the edge, not the control.
        }
        begin();
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
      onKeyDown={(e) => {
        if (e.repeat || (e.key !== ' ' && e.key !== 'Enter')) return;
        // Space scrolls the page otherwise, which moves the row out from under
        // the thing being held.
        e.preventDefault();
        begin();
      }}
      onKeyUp={stop}
      onBlur={stop}
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '.35rem .7rem',
        fontSize: '.85rem',
        fontWeight: 600,
        borderRadius: 5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: C.surface,
        // Same reason as the primary button: the fill is `C.danger`, which is
        // lighter in dark, so white on it drops to 2.96:1. `C.surface` inverts
        // with the theme and holds 4.83 light / 5.87 dark.
        color: progress > 0.55 ? C.surface : ink,
        border: `1px solid ${edge}`,
        // No transition on the colour: it is driven by the same frame loop as
        // the fill, and a CSS transition on top would lag behind the edge that
        // is causing it.
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: `${progress * 100}%`,
          background: fill,
          // Width is set every frame from rAF rather than by a CSS transition,
          // so releasing early snaps back to zero instead of easing back and
          // reading as "it is still going".
          pointerEvents: 'none',
        }}
      />
      <span style={{ position: 'relative' }}>{children}</span>
    </button>
  );
}

/**
 * A timestamp, read the way a person reads one.
 *
 * `3h ago` rather than `11/08/2026, 14:32:07`. In a column of forty rows the
 * locale string is unreadable — every row is the same width and weight, so
 * answering "was this recent" means parsing each one — and it was what this
 * component did until now. See lib/format.ts for where the format switches back
 * to a date, and why.
 *
 * The exact value stays one hover away, because relative time is strictly less
 * precise and an audit trail sometimes needs the second.
 */
export function When({ iso }: { iso: string | Date | null | undefined }) {
  return (
    <span title={exact(iso)} style={{ whiteSpace: 'nowrap' }}>
      {ago(iso)}
    </span>
  );
}

/**
 * A status, as a dot and a word.
 *
 * Distinct from `Badge`: a badge is a label, this is a STATE. The dot carries
 * the colour so the word does not have to be read to get the gist, and the word
 * carries the meaning so the colour does not have to be — which is the same
 * reason it is not a bare coloured square.
 */
export function DotPill({
  tone,
  children,
}: {
  tone: 'good' | 'warn' | 'danger' | 'muted';
  children: ReactNode;
}) {
  const dot = { good: C.successDot, warn: C.warnDot, danger: C.danger, muted: C.mutedDot }[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '.35rem',
        fontSize: '.8rem',
        fontWeight: 600,
        color: C.textMid,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flex: 'none' }}
        aria-hidden
      />
      {children}
    </span>
  );
}

/**
 * An on/off switch: a pill track whose knob slides right for on, with the
 * answer written beside it so the state is never colour alone (Jon,
 * 2026-10-23: off is the default, on reads Yes). For a single yes-or-no
 * setting; a choice between named options is the segmented control instead
 * (ThemeControl, Run history's filters).
 */
export function Switch({
  checked,
  onChange,
  labelledBy,
  onText = 'Yes',
  offText = 'No',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The id of the visible label it answers. */
  labelledBy: string;
  onText?: string;
  offText?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      onClick={() => onChange(!checked)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '.55rem',
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: checked ? C.text : C.textMid,
        fontSize: '.85rem',
        fontWeight: 600,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'relative',
          width: 38,
          height: 22,
          flex: 'none',
          borderRadius: 999,
          background: checked ? C.brand : C.border2,
          // Off is close to the panel in dark mode; the outline keeps the track visible.
          boxShadow: checked ? 'none' : `inset 0 0 0 1px ${C.inputBorder}`,
          transition: 'background-color .15s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: checked ? 19 : 3,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0, 0, 0, .3)',
            transition: 'left .15s',
          }}
        />
      </span>
      {checked ? onText : offText}
    </button>
  );
}

/**
 * Nothing here — said in two lines, not one.
 *
 * "No users match your search" is a fact. "Try a different name, email or role"
 * is the thing that gets somebody unstuck, and leaving it out is what makes an
 * empty screen feel like a dead end rather than a narrow filter. `Note` says
 * only the first half, which is why this exists next to it.
 *
 * This is NOT what a failed request renders as — see `AsyncBoundary`. An empty
 * list means the server said there is nothing; a failure means nobody knows.
 */
export function EmptyState({ title, suggestion }: { title: string; suggestion?: ReactNode }) {
  return (
    <div style={{ padding: '2rem 1.4rem', textAlign: 'center' }}>
      <p style={{ margin: 0, fontWeight: 640, color: C.textMid }}>{title}</p>
      {suggestion ? (
        <p style={{ margin: '.35rem 0 0', color: C.muted, fontSize: '.86rem', lineHeight: 1.5 }}>
          {suggestion}
        </p>
      ) : null}
    </div>
  );
}

/** A figure in a column of figures. Tabular numerals, or `1,847` and `2,502` do
 *  not line up under each other. */
export function Num({ value }: { value: number | null | undefined }) {
  return <span style={numeric}>{count(value)}</span>;
}

/**
 * A form row. Label and control are associated by id, not by nesting, so the
 * label stays clickable when a control is swapped for a different element.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  /**
   * What is wrong with THIS field, from the server.
   *
   * Added for the branding editor (RidgeWill/org-admin#86), where a refused
   * value is the normal case rather than an exception: the login page silently
   * discards anything it cannot use, so a form that could not say which field
   * and why would leave an admin retyping a value forever.
   *
   * It REPLACES the hint rather than sitting beside it. The hint says what the
   * field is for and the error says why this attempt failed; showing both puts
   * the sentence that matters second, under one that no longer helps.
   */
  error?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}>
      <span style={{ fontWeight: 600, color: C.textMid }}>{label}</span>
      {children}
      {error ? (
        <span style={{ color: C.dangerText, fontSize: '.78rem', lineHeight: 1.4 }}>{error}</span>
      ) : (
        hint && <span style={{ color: C.muted, fontSize: '.78rem', lineHeight: 1.4 }}>{hint}</span>
      )}
    </label>
  );
}

export const inputStyle = {
  padding: '.45rem .6rem',
  fontSize: '.88rem',
  fontFamily: 'inherit',
  color: C.text,
  // The card on light, the page on dark: see `--rw-admin-field` in styles.css.
  background: `var(--rw-admin-field, ${C.surface})`,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  width: '100%',
  boxSizing: 'border-box' as const,
};

/*
 * `Reveal` USED TO BE HERE, and its reasoning is worth keeping.
 *
 * It was a `<details>` disclosure holding a form, and every consequential action
 * in this portal sat behind one — on the argument that an admin surface where
 * "publish to every user" is a naked button next to a table is one where it gets
 * clicked by somebody who meant to sort a column.
 *
 * That argument was right and is why it is not simply deleted. What was wrong
 * was the answer: a disclosure anchored BELOW the list put the create action a
 * thousand rows down on Agreements, and a 400-line tabbed detail view does not
 * fit inside a `<details>` inside a table. #63 replaced it with a modal, which
 * asks for confirmation by construction and does not have to live at the bottom
 * of the page to do it. See components/Modal.tsx.
 */

/**
 * The result of a mutation, shown where the mutation was triggered.
 *
 * Distinct states again, and for the same reason as everywhere else here: an
 * action that quietly does nothing on failure is how somebody comes to believe
 * an agreement was published when it was not.
 */
export type Outcome = { ok: true; message: string } | { ok: false; message: string } | null;

export function OutcomeNote({ outcome }: { outcome: Outcome }) {
  if (!outcome) return null;
  return (
    <p
      style={{
        margin: 0,
        padding: '.5rem .7rem',
        borderRadius: 6,
        fontSize: '.83rem',
        lineHeight: 1.45,
        background: outcome.ok ? C.successBg : C.dangerBg,
        color: outcome.ok ? C.successText : C.dangerText,
        border: `1px solid ${outcome.ok ? C.successBg : C.dangerBorder}`,
      }}
    >
      {outcome.message}
    </p>
  );
}

/** An identity that may have been cleared by account closure. Never renders a
 *  bare blank — "closed account" is information, an empty cell is not. */
export function Identity({
  email,
  name,
  pseudonymized,
}: {
  email: string | null;
  name?: string | null;
  pseudonymized?: boolean;
}) {
  if (pseudonymized) return <span style={{ color: C.muted, fontStyle: 'italic' }}>closed account</span>;
  if (!email && !name) return <span style={{ color: C.muted }}>—</span>;
  return (
    <span>
      {email ?? name}
      {/* A real space, not only the margin. Margin is invisible to anything that
          reads the text — a copy-paste into a ticket, or a screen reader — and
          `alice@example.comAlice Smith` is what that produces. */}
      {email && name ? <span style={{ color: C.muted, marginLeft: '.4rem' }}> {name}</span> : null}
    </span>
  );
}

const s = {
  panel: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  panelHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '.9rem 1.2rem',
    borderBottom: `1px solid ${C.border}`,
  },
  /** Title and its info bubble travel together, hard left. */
  panelTitleGroup: { display: 'flex', alignItems: 'center', gap: '.45rem', minWidth: 0 },
  panelTitle: { margin: 0, fontSize: '.95rem', fontWeight: 640 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '.9rem' },
  th: {
    textAlign: 'left' as const,
    padding: '.6rem 1.2rem',
    fontSize: '.72rem',
    letterSpacing: '.06em',
    textTransform: 'uppercase' as const,
    color: C.muted,
    fontWeight: 640,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap' as const,
  },
  /** Minimum width, hard right, and never wrapped — see `actionsLast`. */
  actionCol: {
    width: '1%',
    whiteSpace: 'nowrap' as const,
    textAlign: 'right' as const,
  },
  td: {
    padding: '.7rem 1.2rem',
    borderBottom: `1px solid ${C.rowHover}`,
    verticalAlign: 'middle' as const,
  },
};
